/**
 * The authenticator app: the square a phone scans, and the printed codes that
 * stand in for the phone.
 *
 * For an operator this is not optional and not later. Their first sign-in
 * stops here until a code from the app checks out, and only then is there a
 * session at all — so the codes are shown before the app opens, while nothing
 * else is on screen to click away to.
 */

import { useMemo, useState } from 'react';
import { qrModules } from '@restaurant/ui';

/**
 * Black on white whatever the theme is doing: an inverted QR code is not a
 * QR code to most phones. Drawn by the shared encoder (`@restaurant/ui`'s
 * `qrModules`), never by a library fetched for the purpose — this square
 * carries the secret.
 */
export function QrSquare({ uri }: { uri: string }) {
  const path = useMemo(() => {
    let modules: boolean[][];
    try {
      modules = qrModules(uri);
    } catch {
      // The typed secret beside it covers this.
      return null;
    }
    const quiet = 4;
    let d = '';
    modules.forEach((row, y) => {
      row.forEach((dark, x) => {
        if (dark) d += `M${x + quiet} ${y + quiet}h1v1h-1z`;
      });
    });
    return { d, span: modules.length + quiet * 2 };
  }, [uri]);

  if (!path) return null;
  return (
    <svg
      className="pf__qr"
      viewBox={`0 0 ${path.span} ${path.span}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label="QR code holding the same secret as the text below it"
    >
      <rect width={path.span} height={path.span} fill="#ffffff" />
      <path d={path.d} fill="#000000" />
    </svg>
  );
}

/** The codes, once, with the warning first and a way to copy or print them. */
export function RecoveryCodes({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState<boolean | null>(null);
  return (
    <div className="pf-codes">
      <p className="pf-codes__warn">
        This is the only time you will see these codes. They are stored scrambled; nobody can
        read them back to you. Each one signs you in once in place of the code from your phone.
        Keep them somewhere you can reach without the phone.
      </p>
      <ul className="pf-codes__list">
        {codes.map((code) => (
          <li key={code} className="pf-codes__code">
            {code}
          </li>
        ))}
      </ul>
      <div className="pf__row">
        <button
          type="button"
          className="pf__textbtn"
          onClick={() =>
            navigator.clipboard.writeText(codes.join('\n')).then(
              () => setCopied(true),
              () => setCopied(false),
            )
          }
        >
          Copy them
        </button>
        <button type="button" className="pf__textbtn" onClick={() => window.print()}>
          Print them
        </button>
      </div>
      {copied === true && <p className="pf__sub">Copied. Paste them somewhere safe now.</p>}
      {copied === false && (
        <p className="pf__warn">
          This browser would not let the page copy. Select the codes and copy them by hand, or
          print them.
        </p>
      )}
    </div>
  );
}
